import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  EventEmitter,
  Input,
  Output,
  computed,
  forwardRef,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatInputModule } from '@angular/material/input';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyTagsModule } from '@vendasta/galaxy/tags';
import {
  GalaxyAbstractFilterInput,
  GalaxyFilterOperator,
  GalaxyFilterValueInterface,
} from '@vendasta/galaxy/filter/chips';

/**
 * Operators that take a list rather than a single value. The chips library normally picks a
 * multi-select input for these, but an override replaces that choice, so this component has to
 * cover both shapes itself.
 */
const MULTI_VALUE_OPERATORS: readonly GalaxyFilterOperator[] = [
  GalaxyFilterOperator.FILTER_OPERATOR_IS_ANY,
  GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_ANY,
  GalaxyFilterOperator.FILTER_OPERATOR_IS_ALL,
  GalaxyFilterOperator.FILTER_OPERATOR_IS_NOT_ALL,
];

const MAX_ID_LENGTH = 100;

/**
 * Shared behaviour for identity-field filter inputs.
 *
 * Identity fields hold one distinct value per record, so the options dropdown they would otherwise
 * render is useless to read and expensive to produce: the dropdown calls listFieldOptions, which
 * aggregates the whole object index only to return an arbitrary handful of IDs. Taking the ID as
 * free text skips that call entirely.
 *
 * Overrides are instantiated by the galaxy custom filter input, which only ever assigns `operator`
 * and `initialValue` - the field ID is not passed through. Each identity field therefore needs its
 * own subclass supplying its placeholder.
 */
@Directive()
export abstract class IdFilterInputBaseComponent implements GalaxyAbstractFilterInput {
  /**
   * Placeholder for the input. Deliberately just the ID prefix and an ellipsis: the prefix is the
   * part people leave off when they paste an ID copied out of a URL, and it needs no translation
   * because it contains no prose - it is the literal shape of the value.
   */
  abstract readonly idPlaceholder: string;

  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();

  readonly MAX_ID_LENGTH = MAX_ID_LENGTH;

  private readonly currentOperator = signal<GalaxyFilterOperator | undefined>(undefined);

  /** True when the selected operator takes a list of IDs instead of one. */
  readonly acceptsManyIds = computed(() => {
    const operator = this.currentOperator();
    return operator !== undefined && MULTI_VALUE_OPERATORS.includes(operator);
  });

  /**
   * The operator can change while the component is alive - the chips library assigns it on the
   * existing instance rather than rebuilding the component - so switching between the two shapes
   * has to carry the entered values across.
   */
  @Input() set operator(value: GalaxyFilterOperator | undefined) {
    const wasMulti = this.acceptsManyIds();
    this.currentOperator.set(value);
    const isMulti = this.acceptsManyIds();
    if (wasMulti !== isMulti) {
      this.carryValuesAcross(isMulti);
    }
  }
  get operator(): GalaxyFilterOperator | undefined {
    return this.currentOperator();
  }

  readonly idControl = new FormControl<string>('', { nonNullable: true });
  readonly idsControl = new FormControl<string[]>([], { nonNullable: true });

  @Input() set initialValue(values: GalaxyFilterValueInterface[]) {
    const ids = (values ?? []).map((value) => value?.string ?? '').filter((id) => id.length > 0);
    this.idsControl.setValue(ids, { emitEvent: false });
    this.idControl.setValue(ids[0] ?? '', { emitEvent: false });
  }

  constructor() {
    this.idControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      if (this.acceptsManyIds()) {
        return;
      }
      const trimmed = (value ?? '').trim();
      this.valueChanges.emit(trimmed ? [{ string: trimmed }] : []);
    });
    this.idsControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((ids) => {
      if (!this.acceptsManyIds()) {
        return;
      }
      this.valueChanges.emit((ids ?? []).map((id) => ({ string: id })));
    });
  }

  clearValue(): void {
    this.idControl.setValue('');
    this.idsControl.setValue([]);
  }

  private carryValuesAcross(toMultiValue: boolean): void {
    if (toMultiValue) {
      const single = this.idControl.value.trim();
      this.idsControl.setValue(single ? [single] : []);
      return;
    }
    this.idControl.setValue(this.idsControl.value[0] ?? '');
  }
}

const ID_FILTER_TEMPLATE = `
  <glxy-form-field [bottomSpacing]="'none'">
    @if (acceptsManyIds()) {
      <glxy-tags-input
        [formControl]="idsControl"
        [placeholder]="idPlaceholder"
        [maxTagLength]="MAX_ID_LENGTH"
        [availableTags]="[]"
        [uniqueTags]="true"
        [autocompleteOnly]="false"
        [addOnBlur]="true"
      />
    } @else {
      <input matInput [formControl]="idControl" [placeholder]="idPlaceholder" />
    }
  </glxy-form-field>
`;

const ID_FILTER_IMPORTS = [ReactiveFormsModule, MatInputModule, GalaxyFormFieldModule, GalaxyTagsModule];

@Component({
  selector: 'crm-contact-id-filter-input',
  template: ID_FILTER_TEMPLATE,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: ID_FILTER_IMPORTS,
  providers: [{ provide: GalaxyAbstractFilterInput, useExisting: forwardRef(() => ContactIdFilterInputComponent) }],
})
export class ContactIdFilterInputComponent extends IdFilterInputBaseComponent {
  readonly idPlaceholder = 'ContactID-…';
}

@Component({
  selector: 'crm-company-id-filter-input',
  template: ID_FILTER_TEMPLATE,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: ID_FILTER_IMPORTS,
  providers: [{ provide: GalaxyAbstractFilterInput, useExisting: forwardRef(() => CompanyIdFilterInputComponent) }],
})
export class CompanyIdFilterInputComponent extends IdFilterInputBaseComponent {
  readonly idPlaceholder = 'CompanyID-…';
}

@Component({
  selector: 'crm-opportunity-id-filter-input',
  template: ID_FILTER_TEMPLATE,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: ID_FILTER_IMPORTS,
  providers: [{ provide: GalaxyAbstractFilterInput, useExisting: forwardRef(() => OpportunityIdFilterInputComponent) }],
})
export class OpportunityIdFilterInputComponent extends IdFilterInputBaseComponent {
  readonly idPlaceholder = 'OpportunityID-…';
}

@Component({
  selector: 'crm-custom-object-id-filter-input',
  template: ID_FILTER_TEMPLATE,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: ID_FILTER_IMPORTS,
  providers: [
    { provide: GalaxyAbstractFilterInput, useExisting: forwardRef(() => CustomObjectIdFilterInputComponent) },
  ],
})
export class CustomObjectIdFilterInputComponent extends IdFilterInputBaseComponent {
  readonly idPlaceholder = 'CustomObjectID-…';
}

@Component({
  selector: 'crm-activity-id-filter-input',
  template: ID_FILTER_TEMPLATE,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: ID_FILTER_IMPORTS,
  providers: [{ provide: GalaxyAbstractFilterInput, useExisting: forwardRef(() => ActivityIdFilterInputComponent) }],
})
export class ActivityIdFilterInputComponent extends IdFilterInputBaseComponent {
  readonly idPlaceholder = 'ActivityID-…';
}
